import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';
import api, { cartApi } from '../services/api';
import { DEFAULT_COUNTRY_CODE } from '../constants/countries';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // Active academic region for the public catalog. Default is the registered
  // user's country_code once logged in (chosen at registration), or IP
  // geolocation for anonymous visitors (the effect below). A manual region switch
  // is a SESSION-ONLY override, held in sessionStorage so it survives page
  // navigation but is forgotten on the next login, at which point the registered
  // preference (or IP, if anonymous) reasserts the default.
  const [currentCountry, setCurrentCountryState] = useState(
    () => sessionStorage.getItem('session_country_override') || DEFAULT_COUNTRY_CODE
  );

  // API base URL
  const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8008').replace(/\/+$/, '');

  // Define logout function early to avoid hoisting issues
  const logout = useCallback(async () => {
    try {
      // Call backend logout endpoint
      if (token) {
        await axios.post(`${API_BASE_URL}/api/auth/logout`);
      }
    } catch (error) {
      // Continue with logout even if backend call fails
      console.warn('Logout API call failed:', error);
    } finally {
      // Clear local auth state
      Cookies.remove('authToken');
      localStorage.removeItem('user');
      localStorage.removeItem('unify_cart')
      // Forget any session-only region switch so the next login re-detects by IP.
      sessionStorage.removeItem('session_country_override');
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);
      setCurrentCountryState(DEFAULT_COUNTRY_CODE);
      // toast.success('You have been logged out successfully.');
    }
  }, [token, API_BASE_URL]);

  // Set up axios interceptors
  useEffect(() => {
    // Request interceptor to add auth token
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const authToken = token || Cookies.get('authToken');
        if (authToken) {
          config.headers.Authorization = `Bearer ${authToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle auth errors
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token expired or invalid
          logout();
          // toast.error('Your session has expired. Please log in again.');
        }
        return Promise.reject(error);
      }
    );

    // Cleanup interceptors
    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [token, logout]);

  // Initialize auth state from stored token
  useEffect(() => {
    const validateToken = async (authToken) => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/auth/validate-token`, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        return response.data;
      } catch (error) {
        throw new Error('Invalid token');
      }
    };

    const initAuth = async () => {
      const storedToken = Cookies.get('authToken');
      const storedUser = localStorage.getItem('user');

      if (storedToken && storedUser) {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
        setIsAuthenticated(true);

        // Validate token with backend and sync user data
        try {
          const validatedData = await validateToken(storedToken);
          if (validatedData && validatedData.user) {
            setUser(validatedData.user);
            localStorage.setItem('user', JSON.stringify(validatedData.user));
          }
        } catch (error) {
          // Token is invalid, clear auth state
          logout();
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [API_BASE_URL, logout]);

  const login = useCallback(async (email, password) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API_BASE_URL}/api/auth/login`, {
        email,
        password
      });

      const { access_token, user: userData } = response.data;

      // Store token and user data
      Cookies.set('authToken', access_token, {
        expires: 1, // 1 day
        secure: process.env.NODE_ENV === 'production',
        sameSite: 'strict'
      });
      localStorage.setItem('user', JSON.stringify(userData));

      setToken(access_token);
      setUser(userData);
      setIsAuthenticated(true);

      toast.success(`Welcome back, ${userData.full_name}!`);
      // Auto-add pending course to cart after login
      try {
        const pending = localStorage.getItem('pending_add_to_cart');
        if (pending) {
          await cartApi.add(pending);
          localStorage.removeItem('pending_add_to_cart');
          toast.success('Added to cart');
        }
      } catch (e) {
        console.warn('Pending add-to-cart after login failed:', e);
      }
      return { success: true, user: userData };
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      // toast.error(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL]);

  const register = useCallback(async (email, mobile, full_name) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API_BASE_URL}/api/auth/register`, {
        email,
        mobile,
        full_name
      });

      const { access_token, user: userData } = response.data;

      // Store token and user data
      Cookies.set('authToken', access_token, {
        expires: 1, // 1 day
        secure: process.env.NODE_ENV === 'production',
        sameSite: 'strict'
      });
      localStorage.setItem('user', JSON.stringify(userData));

      setToken(access_token);
      setUser(userData);
      setIsAuthenticated(true);

      toast.success(`Welcome, ${userData.full_name}! Your account has been created.`);
      // Auto-add pending course to cart after registration
      try {
        const pending = localStorage.getItem('pending_add_to_cart');
        if (pending) {
          await cartApi.add(pending);
          localStorage.removeItem('pending_add_to_cart');
          toast.success('Added to cart');
        }
      } catch (e) {
        console.warn('Pending add-to-cart after register failed:', e);
      }
      return { success: true, user: userData };
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL]);

  const requestOTP = useCallback(async (identifier) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API_BASE_URL}/api/auth/request-otp`, {
        identifier
      });

      toast.success('OTP sent successfully!');
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to send OTP';
      // toast.error(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL]);

  const loginWithOTP = useCallback(async (identifier, otp) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API_BASE_URL}/api/auth/verify-otp`, {
        identifier,
        otp
      });

      const { access_token, user: userData } = response.data;

      // Store token and user data
      Cookies.set('authToken', access_token, {
        expires: 1, // 1 day
        secure: process.env.NODE_ENV === 'production',
        sameSite: 'strict'
      });
      localStorage.setItem('user', JSON.stringify(userData));

      setToken(access_token);
      setUser(userData);
      setIsAuthenticated(true);

      toast.success(`Welcome back, ${userData.full_name}!`);
      return { success: true, user: userData };
    } catch (error) {
      const message = error.response?.data?.detail || 'OTP verification failed';
      // toast.error(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL]);

  const updateUser = useCallback((userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  }, []);

  // Updated role helpers for new multi-role system
  const isAdmin = useCallback(() => {
    const currentRole = user?.active_role || user?.role;
    return currentRole === 'admin' || user?.roles?.includes('admin');
  }, [user]);

  const isSuperAdmin = useCallback(() => {
    // Note: super_admin is now 'admin' in new system
    const currentRole = user?.active_role || user?.role;
    return currentRole === 'admin' || user?.roles?.includes('admin');
  }, [user]);

  const isReviewer = useCallback(() => {
    const currentRole = user?.active_role || user?.role;
    return currentRole === 'reviewer' || user?.roles?.includes('reviewer');
  }, [user]);

  const isCreator = useCallback(() => {
    const currentRole = user?.active_role || user?.role;
    return currentRole === 'creator' || user?.roles?.includes('creator');
  }, [user]);

  const isApprover = useCallback(() => {
    const currentRole = user?.active_role || user?.role;
    return currentRole === 'approver' || user?.roles?.includes('approver');
  }, [user]);

  const isCandidate = useCallback(() => {
    const currentRole = user?.active_role || user?.role;
    return currentRole === 'candidate' || user?.roles?.includes('candidate');
  }, [user]);

  const isStudent = useCallback(() => {
    const currentRole = user?.active_role || user?.role;
    return currentRole === 'student' || user?.roles?.includes('student');
  }, [user]);

  const hasRole = useCallback((requiredRole) => {
    const currentRole = user?.active_role || user?.role;
    return currentRole === requiredRole || user?.roles?.includes(requiredRole);
  }, [user]);

  const hasActiveRole = useCallback((requiredRole) => {
    const currentRole = user?.active_role || user?.role;
    return currentRole === requiredRole;
  }, [user]);

  // Function to refresh user data from backend
  const refreshUser = useCallback(async () => {
    const storedToken = Cookies.get('authToken');
    if (storedToken) {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/auth/validate-token`, {
          headers: { Authorization: `Bearer ${storedToken}` }
        });
        if (response.data.valid && response.data.user) {
          setUser(response.data.user);
          localStorage.setItem('user', JSON.stringify(response.data.user));
        }
      } catch (error) {
        console.error('Failed to refresh user data:', error);
      }
    }
  }, [API_BASE_URL]);

  // Default catalog region. On mount and on every auth change (login / logout /
  // session-restore) the active region is resolved in this order:
  //   1. a session-only manual switch (setCurrentCountry) - wins for this session
  //   2. the registered user's preferred country (user.country_code, chosen at
  //      registration) - the default for a logged-in student, regardless of IP
  //   3. IP geolocation - only for anonymous visitors before login
  //   4. the default region - fallback
  // A logged-in student keeps the catalog for the country they registered with,
  // so studying abroad never flips it. The session override is cleared on logout
  // / new browser session, so the registered preference reasserts on next login.
  // Best-effort: any failure keeps the default.
  useEffect(() => {
    // A manual switch this session takes precedence; do not re-resolve over it.
    const override = sessionStorage.getItem('session_country_override');
    if (override) {
      setCurrentCountryState(override);
      return;
    }

    // Logged-in student: the catalog follows their registered country, not IP.
    if (isAuthenticated && user?.country_code) {
      setCurrentCountryState(user.country_code);
      return;
    }

    // Anonymous visitor: fall back to IP geolocation for a sensible default.
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(`${API_BASE_URL}/api/courses/detect-country`);
        const code = resp.data?.country_code;
        if (!cancelled && resp.data?.detected && code) {
          setCurrentCountryState(code);
        } else if (!cancelled) {
          setCurrentCountryState(DEFAULT_COUNTRY_CODE);
        }
      } catch (error) {
        // Best-effort only: keep the default region on any failure.
      }
    })();
    return () => { cancelled = true; };
    // Re-run whenever auth state or the registered country settles.
  }, [isAuthenticated, user?.country_code, API_BASE_URL]);

  // Change the active catalog region for the CURRENT SESSION only. Updates state
  // immediately (instant catalog reload, no refresh) and records a session-only
  // override in sessionStorage so it survives page navigation. It is deliberately
  // NOT persisted to the user profile: on the next login the override is gone and
  // the registered preference (or IP, if anonymous) reasserts the default.
  // user.country_code is left unchanged and continues to serve other features.
  const setCurrentCountry = useCallback((code) => {
    setCurrentCountryState(code);
    sessionStorage.setItem('session_country_override', code);
  }, []);

  const value = useMemo(() => ({
    user,
    token,
    loading,
    isAuthenticated,
    currentCountry,
    setCurrentCountry,
    login,
    register,
    requestOTP,
    loginWithOTP,
    logout,
    updateUser,
    refreshUser,
    setToken,
    setUser,
    setIsAuthenticated,
    isAdmin,
    isSuperAdmin,
    isReviewer,
    isCreator,
    isApprover,
    isCandidate,
    isStudent,
    hasRole,
    hasActiveRole,
    API_BASE_URL
  }), [user, token, loading, isAuthenticated, currentCountry, setCurrentCountry, login, register, requestOTP, loginWithOTP, logout, updateUser, refreshUser, isAdmin, isSuperAdmin, isReviewer, isCreator, isApprover, isCandidate, isStudent, hasRole, hasActiveRole, API_BASE_URL]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;